<script setup>
import { onBeforeUnmount, watch, ref } from 'vue';
import InternalCommunicationAPI from 'dashboard/api/internalCommunication';

const props = defineProps({
  attachments: {
    type: Array,
    default: () => [],
  },
});

const previewUrls = ref({});

const revokePreviews = () => {
  Object.values(previewUrls.value).forEach(url => URL.revokeObjectURL(url));
  previewUrls.value = {};
};

const loadPreviews = async () => {
  revokePreviews();
  const previewable = props.attachments.filter(item => item.previewable);
  await Promise.all(
    previewable.map(async attachment => {
      try {
        const { data } = await InternalCommunicationAPI.downloadAttachment(
          attachment.preview_url
        );
        previewUrls.value[attachment.id] = URL.createObjectURL(data);
      } catch {
        previewUrls.value[attachment.id] = null;
      }
    })
  );
};

const download = async attachment => {
  const { data } = await InternalCommunicationAPI.downloadAttachment(
    attachment.download_url
  );
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = attachment.filename;
  link.click();
  URL.revokeObjectURL(url);
};

watch(() => props.attachments, loadPreviews, { immediate: true });
onBeforeUnmount(revokePreviews);
</script>

<template>
  <div v-show="attachments.length" class="grid gap-2 pt-2">
    <button
      v-for="attachment in attachments"
      :key="attachment.id"
      type="button"
      class="flex max-w-sm items-center gap-3 overflow-hidden rounded-lg border border-n-weak bg-n-alpha-1 p-2 text-left transition-all duration-100 ease-out hover:bg-n-alpha-2 focus-visible:border-n-brand focus-visible:outline-none active:scale-[0.99]"
      @click="download(attachment)"
    >
      <img
        v-if="previewUrls[attachment.id]"
        :src="previewUrls[attachment.id]"
        :alt="attachment.filename"
        class="size-16 rounded-md object-cover"
      />
      <span
        v-else
        class="flex size-10 flex-shrink-0 items-center justify-center rounded-md bg-n-slate-3"
      >
        <i class="i-lucide-file size-5 text-n-slate-11" />
      </span>
      <span class="min-w-0">
        <span class="block truncate text-sm font-medium text-n-slate-12">
          {{ attachment.filename }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{
            $t('INTERNAL_COMMUNICATION.COMPOSER.FILE_SIZE_KB', {
              size: Math.ceil(attachment.byte_size / 1024),
            })
          }}
        </span>
      </span>
    </button>
  </div>
</template>
